import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function SepilSpray() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const keyDifferentiators = t("sepilPage.keyDifferentiators", { returnObjects: true }) || [];
  const clinicalApplications = t("sepilPage.clinicalApplications", { returnObjects: true }) || [];
  const fifteenStrengths = t("sepilPage.fifteenStrengths", { returnObjects: true }) || [];
  const pathogens = t("sepilPage.capabilities.pathogens", { returnObjects: true }) || [];
  const mechanismSteps = t("sepilPage.mechanism.steps", { returnObjects: true }) || [];
  const directionsItems = t("sepilPage.directions.items", { returnObjects: true }) || [];
  const safetyItems = t("sepilPage.safety.items", { returnObjects: true }) || [];

  return (
    <div style={{ fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif', color: "#2d3748", backgroundColor: "#f7fafc", paddingBottom: "80px" }}>
      
      {/* HERO HEADER */}
      <section style={{ background: "linear-gradient(135deg, #0f172a 0%, #0d9488 100%)", color: "#ffffff", padding: "120px 20px 70px", textAlign: "center" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <span style={{ background: "#0ea5e9", color: "#ffffff", padding: "6px 16px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: "700", letterSpacing: "1px", textTransform: "uppercase" }}>
            {t("sepilPage.hero.badge")}
          </span>
          <h1 style={{ fontSize: "3.5rem", margin: "20px 0 10px", fontWeight: "800", color: "#ffffff", letterSpacing: "-1px" }}>{t("sepilPage.hero.title")}</h1>
          <p style={{ fontSize: "1.35rem", opacity: "0.95", maxWidth: "750px", margin: "0 auto 35px", lineHeight: "1.5", fontWeight: "300" }}>
            {t("sepilPage.hero.subtitle")}
          </p>
          <button 
            onClick={() => navigate("/products")}
            style={{ padding: "14px 35px", fontSize: "1.1rem", background: "#0ea5e9", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", boxShadow: "0 4px 14px rgba(14, 165, 233, 0.4)", transition: "background 0.2s" }}
            onMouseEnter={(e) => e.target.style.background = "#0284c7"}
            onMouseLeave={(e) => e.target.style.background = "#0ea5e9"}
          >
            {t("sepilPage.hero.btnOrder")}
          </button>
        </div>
      </section>

      {/* INTRODUCTION CONTAINER & AWARD BANNER */}
      <section style={{ maxWidth: "1000px", margin: "-30px auto 50px", padding: "40px", background: "#ffffff", borderRadius: "12px", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
        
        {/* Award Highlight */}
        <div style={{ display: "flex", alignItems: "center", background: "#f0fdfa", border: "1px solid #ccfbf1", padding: "15px 20px", borderRadius: "8px", marginBottom: "30px" }}>
          <span style={{ fontSize: "1.5rem", marginRight: "12px" }}>🏆</span>
          <p style={{ margin: 0, color: "#115e59", fontWeight: "600", fontSize: "0.95rem" }}>
            {t("sepilPage.award")}
          </p>
        </div>

        <h2 style={{ color: "#0f172a", borderBottom: "2px solid #edf2f7", paddingBottom: "12px", marginTop: 0 }}>{t("sepilPage.intro.title")}</h2>
        <p style={{ lineHeight: "1.7", color: "#4a5568" }}>
          {t("sepilPage.intro.p1")}
        </p>
        <p style={{ lineHeight: "1.7", color: "#4a5568", marginTop: "15px" }}>
          <strong>{t("sepilPage.intro.p2Pre")}</strong> {t("sepilPage.intro.p2Post")}
        </p>
        <p style={{ lineHeight: "1.7", color: "#4a5568", marginTop: "15px" }}>
          {t("sepilPage.intro.p3")}
        </p>
      </section>

      {/* THE SCIENCE & DIFFERENTIATORS */}
      <section style={{ maxWidth: "1000px", margin: "0 auto 50px", padding: "0 20px" }}>
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <h2 style={{ color: "#0f172a", fontSize: "2rem" }}>{t("sepilPage.science.title")}</h2>
          <p style={{ color: "#718096", maxWidth: "800px", margin: "10px auto 0", lineHeight: "1.6" }}>
            {t("sepilPage.science.desc")}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))", gap: "25px" }}>
          
          {/* Pillar 1 */}
          <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", boxShadow: "0 4px 15px rgba(0,0,0,0.02)", border: "1px solid #e2e8f0" }}>
            <h3 style={{ color: "#0d9488", fontSize: "1.25rem", marginTop: 0 }}>{t("sepilPage.science.pillar1Title")}</h3>
            <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6", margin: 0 }}>
              {t("sepilPage.science.pillar1Desc")}
            </p>
          </div>

          {/* Pillar 2 */}
          <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", boxShadow: "0 4px 15px rgba(0,0,0,0.02)", border: "1px solid #e2e8f0" }}>
            <h3 style={{ color: "#0d9488", fontSize: "1.25rem", marginTop: 0 }}>{t("sepilPage.science.pillar2Title")}</h3>
            <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6", margin: 0 }}>
              {t("sepilPage.science.pillar2Desc")}
            </p>
          </div>

          {/* Pillar 3 */}
          <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", boxShadow: "0 4px 15px rgba(0,0,0,0.02)", border: "1px solid #e2e8f0" }}>
            <h3 style={{ color: "#0d9488", fontSize: "1.25rem", marginTop: 0 }}>{t("sepilPage.science.pillar3Title")}</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
              {keyDifferentiators.slice(0, 6).map((diff, i) => (
                <span key={i} style={{ backgroundColor: "#f1f5f9", color: "#334155", fontSize: "0.8rem", padding: "4px 10px", borderRadius: "12px", fontWeight: "500" }}>
                  ✓ {diff}
                </span>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* DETAILED CORE CAPABILITIES */}
      <section style={{ backgroundColor: "#f1f5f9", padding: "60px 20px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", color: "#0f172a", fontSize: "2rem", marginBottom: "40px" }}>{t("sepilPage.capabilities.title")}</h2>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))", gap: "25px" }}>
            
            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#0f172a", fontSize: "1.1rem" }}>{t("sepilPage.capabilities.c1Title")}</h4>
              <p style={{ margin: "0 0 12px 0", color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("sepilPage.capabilities.c1Desc")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {pathogens.map((pathogen, pIdx) => (
                  <span key={pIdx} style={{ fontSize: "0.8rem", color: "#ef4444", background: "#fef2f2", padding: "3px 8px", borderRadius: "4px", fontWeight: "600", border: "1px solid #fee2e2" }}>
                    • {pathogen}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#0f172a", fontSize: "1.1rem" }}>{t("sepilPage.capabilities.c2Title")}</h4>
              <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("sepilPage.capabilities.c2Desc")}
              </p>
            </div>

            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#0f172a", fontSize: "1.1rem" }}>{t("sepilPage.capabilities.c3Title")}</h4>
              <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("sepilPage.capabilities.c3Desc")}
              </p>
            </div>

            <div style={{ background: "#ffffff", padding: "25px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#0f172a", fontSize: "1.1rem" }}>{t("sepilPage.capabilities.c4Title")}</h4>
              <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                {t("sepilPage.capabilities.c4Desc")}
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* CLINICAL APPLICATIONS */}
      <section style={{ maxWidth: "1000px", margin: "60px auto", padding: "0 20px" }}>
        <h2 style={{ textAlign: "center", color: "#0f172a", marginBottom: "10px" }}>{t("sepilPage.indications.title")}</h2>
        <p style={{ textAlign: "center", color: "#718096", marginBottom: "35px" }}>{t("sepilPage.indications.subtitle")}</p>
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
          {clinicalApplications.map((app, idx) => (
            <div key={idx} style={{ background: "#ffffff", padding: "20px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 8px 0", color: "#0d9488", fontWeight: "700" }}>{app.title}</h4>
              <p style={{ margin: 0, fontSize: "0.9rem", color: "#4a5568", lineHeight: "1.5" }}>{app.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MECHANISM OF ACTION (STEPS) */}
      <section style={{ maxWidth: "900px", margin: "0 auto 60px", padding: "0 20px" }}>
        <h2 style={{ textAlign: "center", color: "#0f172a", marginBottom: "35px" }}>{t("sepilPage.mechanism.title")}</h2>
        <div style={{ background: "#ffffff", padding: "35px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
          <p style={{ margin: "0 0 25px 0", color: "#4a5568", lineHeight: "1.6" }}>
            {t("sepilPage.mechanism.desc")}
          </p>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {mechanismSteps.map((m, mIdx) => (
              <div key={mIdx} style={{ display: "flex", gap: "15px" }}>
                <div style={{ background: "#0d9488", color: "white", width: "28px", height: "28px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", flexShrink: 0, fontSize: "0.9rem" }}>
                  {mIdx + 1}
                </div>
                <div>
                  <strong style={{ color: "#0f172a" }}>{m.title}:</strong> <span style={{ color: "#4a5568", fontSize: "0.95rem" }}>{m.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 15 KEY STRENGTHS TABLE MATRIX */}
      <section style={{ maxWidth: "1000px", margin: "60px auto", padding: "0 20px" }}>
        <h2 style={{ textAlign: "center", color: "#0f172a", marginBottom: "30px" }}>{t("sepilPage.strengths.title")}</h2>
        <div style={{ background: "#ffffff", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)", overflow: "hidden", border: "1px solid #e2e8f0" }}>
          <div style={{ backgroundColor: "#0f172a", color: "#ffffff", padding: "15px 20px", fontWeight: "700", fontSize: "1.1rem" }}>
            {t("sepilPage.strengths.header")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", padding: "10px" }}>
            {fifteenStrengths.map((strength, sIdx) => (
              <div key={sIdx} style={{ display: "flex", alignItems: "center", padding: "12px 15px", borderBottom: "1px solid #f1f5f9", fontSize: "0.9rem", color: "#334155" }}>
                <span style={{ color: "#0d9488", marginRight: "10px", fontWeight: "bold" }}>⚡</span>
                {strength}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIRECTIONS FOR USE & SAFETY METRICS */}
      <section style={{ maxWidth: "1000px", margin: "0 auto 40px", padding: "0 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "30px" }}>
        
        {/* DIRECTIONS FOR USE */}
        <div style={{ background: "#ffffff", padding: "30px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ color: "#0f172a", marginTop: 0, marginBottom: "15px" }}>{t("sepilPage.directions.title")}</h3>
          <ol style={{ paddingLeft: "20px", color: "#4a5568", lineHeight: "1.8", margin: 0, fontSize: "0.95rem" }}>
            {directionsItems.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ol>

          <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px dashed #e2e8f0" }}>
            <strong style={{ color: "#0d9488", display: "block", marginBottom: "5px" }}>{t("sepilPage.directions.frequencyTitle")}</strong>
            <p style={{ margin: 0, color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
              {t("sepilPage.directions.frequencyPre")}<strong>{t("sepilPage.directions.frequencyBold")}</strong>{t("sepilPage.directions.frequencyPost")}
            </p>
          </div>
        </div>

        {/* SAFETY PROFILE */}
        <div style={{ background: "#f0fdfa", border: "1px solid #ccfbf1", padding: "30px", borderRadius: "10px" }}>
          <h3 style={{ color: "#115e59", marginTop: 0, marginBottom: "15px" }}>{t("sepilPage.safety.title")}</h3>
          <p style={{ color: "#115e59", fontSize: "0.95rem", lineHeight: "1.6", marginBottom: "15px" }}>
            {t("sepilPage.safety.desc")}
          </p>
          <ul style={{ paddingLeft: "20px", color: "#134e4a", lineHeight: "1.8", margin: 0, fontSize: "0.95rem" }}>
            {safetyItems.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>

      </section>

      {/* OUTRO CONCLUSION */}
      <section style={{ maxWidth: "800px", margin: "60px auto 0", padding: "0 20px", textAlign: "center" }}>
        <h2 style={{ color: "#0f172a", fontSize: "1.75rem", marginBottom: "15px" }}>{t("sepilPage.conclusion.title")}</h2>
        <p style={{ color: "#4a5568", lineHeight: "1.7", fontSize: "1rem" }}>
          {t("sepilPage.conclusion.p1")}
        </p>
        <p style={{ color: "#4a5568", lineHeight: "1.7", fontSize: "1rem", marginTop: "15px" }}>
          {t("sepilPage.conclusion.p2")}
        </p>
        
        {/* Bottom Navigation Button */}
        <button 
          onClick={() => navigate("/products")}
          style={{ marginTop: "35px", padding: "14px 40px", fontSize: "1.1rem", background: "#0d9488", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", boxShadow: "0 4px 12px rgba(13, 148, 136, 0.3)" }}
        >
          {t("sepilPage.conclusion.btnReturn")}
        </button>
      </section>

    </div>
  );
}

export default SepilSpray;